/**
 * Image Optimization Utilities
 * Handles Cloudinary optimization and responsive images
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Get full image URL
 */
export const getImageUrl = (imageUrl) => {
  // Type check - ensure imageUrl is a string
  if (!imageUrl || typeof imageUrl !== 'string') {
    // If it's an array, take the first element
    if (Array.isArray(imageUrl) && imageUrl.length > 0) {
      imageUrl = imageUrl[0];
      if (typeof imageUrl !== 'string') return '';
    } else {
      return '';
    }
  }
  
  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If it's a relative path starting with /api or /uploads, prepend backend URL
  if (imageUrl.startsWith('/api/') || imageUrl.startsWith('/uploads/')) {
    return `${BACKEND_URL}${imageUrl}`;
  }
  
  // Otherwise return as is
  return imageUrl;
};

/**
 * Optimize Cloudinary URL with automatic format, quality, and size
 * @param {string} imageUrl - Original image URL
 * @param {object} options - Optimization options
 * @returns {string} Optimized URL
 */
export const optimizeCloudinaryUrl = (imageUrl, options = {}) => {
  const {
    width = 'auto',
    quality = 'auto:good',
    format = 'auto',
    crop = 'fill',
    gravity = 'auto',
    fetchFormat = true,
  } = options;

  const baseUrl = getImageUrl(imageUrl);
  
  // Check if it's a Cloudinary URL
  if (baseUrl.includes('res.cloudinary.com')) {
    try {
      const url = new URL(baseUrl);
      const pathParts = url.pathname.split('/upload/');
      
      if (pathParts.length === 2) {
        // Build transformation string
        const transformations = [
          `f_${format}`,
          `q_${quality}`,
          width !== 'none' ? `w_${width}` : null,
          'dpr_auto',
          'c_' + crop,
          'g_' + gravity,
          'fl_progressive',
          'fl_lossy',
        ].filter(Boolean).join(',');
        
        // Reconstruct URL with optimizations
        const optimizedPath = `${pathParts[0]}/upload/${transformations}/${pathParts[1]}`;
        url.pathname = optimizedPath;
        
        return url.toString();
      }
    } catch (e) {
      console.warn('Failed to optimize Cloudinary URL:', e);
      return baseUrl;
    }
  }
  
  // For Unsplash images
  if (baseUrl.includes('unsplash.com')) {
    try {
      const url = new URL(baseUrl);
      if (width !== 'none') {
        url.searchParams.set('w', width === 'auto' ? '800' : width);
      }
      url.searchParams.set('q', '75'); // Reduced from 80 for better compression
      url.searchParams.set('fm', 'webp'); // Force WebP format
      url.searchParams.set('auto', 'compress'); // Better compression
      url.searchParams.set('fit', 'crop');
      return url.toString();
    } catch (e) {
      return baseUrl;
    }
  }
  
  return baseUrl;
};

/**
 * Generate responsive srcSet for images
 */
export const generateSrcSet = (imageUrl, sizes = [400, 600, 800, 1200]) => {
  return sizes
    .map(size => `${optimizeCloudinaryUrl(imageUrl, { width: size })} ${size}w`)
    .join(', ');
};

/**
 * Generate blur placeholder (LQIP - Low Quality Image Placeholder)
 */
export const getBlurDataUrl = (imageUrl) => {
  const baseUrl = getImageUrl(imageUrl);
  
  if (baseUrl.includes('res.cloudinary.com')) {
    return optimizeCloudinaryUrl(imageUrl, {
      width: 40,
      quality: 'auto:low',
      format: 'auto',
      crop: 'fill',
    });
  }
  
  if (baseUrl.includes('unsplash.com')) {
    try {
      const url = new URL(baseUrl);
      url.searchParams.set('w', '40');
      url.searchParams.set('q', '30');
      url.searchParams.set('blur', '10');
      url.searchParams.set('auto', 'format');
      return url.toString();
    } catch (e) {
      return baseUrl;
    }
  }
  
  return baseUrl;
};

/**
 * Get responsive image sizes attribute
 */
export const getImageSizes = (type = 'card') => {
  const sizeMaps = {
    hero: '100vw',
    card: '(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw',
    detail: '(max-width: 768px) 100vw, 66vw',
    thumbnail: '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw',
  };
  
  return sizeMaps[type] || sizeMaps.card;
};

/**
 * Preload critical images
 */
export const preloadImage = (imageUrl, options = {}) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = optimizeCloudinaryUrl(imageUrl, options);
  
  if (options.imageSrcset) {
    link.imageSrcset = generateSrcSet(imageUrl);
  }
  
  if (options.imageSizes) {
    link.imageSizes = getImageSizes(options.type);
  }
  
  document.head.appendChild(link);
};
